import React from 'react'
import InlineHelp from '../ui/InlineHelp'

const PriceBundledHelp = ({ title }: { title: string }) => {
  const content = `
        Bundled power includes energy, enviromental attributes (RECs and other) and facility attributes revenue
        (including capacity and ancillary services), where applicable.
    `
  return <InlineHelp title={title} content={content}/>
}

const CreditRatingHelp = ({ title }: { title: string }) => {
  const content =
    <div className="space-y-4">
      <table className="w-full divide-y divide-gray-300">
        <thead className="bg-gray-100">
          <tr>
            <th
              scope="col"
              className="px-4 py-1"
            >
            </th>
            <th
              scope="col"
              className="px-1 py-1 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wide"
            >
            Agency Rating
            </th>
            <th
              scope="col"
              className="px-1 py-1 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wide"
            >
            Description
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-300">
          <tr>
            <td className="px-1 py-1">1</td>
            <td className="px-1 py-1 whitespace-nowrap text-sm text-gray-500">
              <div><span className="font-medium">Moody's:</span> Aaa - Aa3</div>
              <div><span className="font-medium">S&P:</span> AAA - AA-</div>
              <div><span className="font-medium">Fitch:</span> AAA - AA-</div>
            </td>
            <td className="px-1 py-1 whitespace-nowrap text-sm text-gray-500">High Grade</td>
          </tr>
          <tr>
            <td className="px-1 py-1">2</td>
            <td className="px-1 py-1 whitespace-nowrap text-sm text-gray-500">
              <div><span className="font-medium">Moody's:</span> A1 - A3</div>
              <div><span className="font-medium">S&P:</span> A+ - A-</div>
              <div><span className="font-medium">Fitch:</span> A+ - AA-</div>
            </td>
            <td className="px-1 py-1 whitespace-nowrap text-sm text-gray-500">Upper Medium Grade</td>
          </tr>
          <tr>
            <td className="px-1 py-1">3</td>
            <td className="px-1 py-1 whitespace-nowrap text-sm text-gray-500">
              <div><span className="font-medium">Moody's:</span> Baa1 - Baa3</div>
              <div><span className="font-medium">S&P:</span> BBB+ - BBB-</div>
              <div><span className="font-medium">Fitch:</span> BBB+ - BBB-</div>
            </td>
            <td className="px-1 py-1 whitespace-nowrap text-sm text-gray-500">Lower Medium Grade</td>
          </tr>
          <tr>
            <td className="px-1 py-1">4</td>
            <td className="px-1 py-1 whitespace-nowrap text-sm text-gray-500">
              <div><span className="font-medium">Moody's:</span> Ba1 - Ba3</div>
              <div><span className="font-medium">S&P:</span> BB+ - BB-</div>
              <div><span className="font-medium">Fitch:</span> BB+ - BB-</div>
            </td>
            <td className="px-1 py-1 whitespace-nowrap text-sm text-gray-500">Speculative</td>
          </tr>
          <tr>
            <td className="px-1 py-1">5</td>
            <td className="px-1 py-1 whitespace-nowrap text-sm text-gray-500">
              <div><span className="font-medium">Moody's:</span> B1 - B3</div>
              <div><span className="font-medium">S&P:</span> B+ - B-</div>
              <div><span className="font-medium">Fitch:</span> B+ - B-</div>
            </td>
            <td className="px-1 py-1 whitespace-nowrap text-sm text-gray-500">Highly Speculative</td>
          </tr>
          <tr>
            <td className="px-1 py-1">6</td>
            <td className="px-1 py-1 whitespace-nowrap text-sm text-gray-500">
              <div><span className="font-medium">Moody's:</span> Caa1 - Caa</div>
              <div><span className="font-medium">S&P:</span> CCC+ - C</div>
              <div><span className="font-medium">Fitch:</span> C</div>
            </td>
            <td className="px-1 py-1 whitespace-nowrap text-sm text-gray-500">Substantial Risks</td>
          </tr>
          <tr>
            <td className="px-1 py-1">7</td>
            <td className="px-1 py-1 whitespace-nowrap text-sm text-gray-500">
              <div><span className="font-medium">Moody's:</span> C</div>
              <div><span className="font-medium">S&P:</span> D</div>
              <div><span className="font-medium">Fitch:</span> DDD - D</div>
            </td>
            <td className="px-1 py-1 whitespace-nowrap text-sm text-gray-500">In Default</td>
          </tr>
          <tr>
            <td className="px-1 py-1">8</td>
            <td className="px-1 py-1 whitespace-nowrap text-sm text-gray-500">
              <div><span className="font-medium">Moody's:</span> NR</div>
              <div><span className="font-medium">S&P:</span> NR</div>
              <div><span className="font-medium">Fitch:</span> NR</div>
            </td>
            <td className="px-1 py-1 whitespace-nowrap text-sm text-gray-500">Not Rated</td>
          </tr>
        </tbody>
      </table>
    </div>

  return <InlineHelp title={title} content={content}/>
}

const DisruptionFallbackHelp = ({ title }: { title: string }) => {
  const content = <>
    <p>
      In the situation where one of the reference prices used in this deal ceases to be available, please select
      the method to be used to resolve that issue. The three choices have the following definitions:
    </p>
    <p>
      (a) &quot;Fallback Reference Price&quot; means that the Calculation Agent shall determine the Floating
      Price, using the most physically proximate floating price source to the Floating Price that is not subject
      to a Market Disruption Event.
    </p>
    <p>
      (b) &quot;Negotiated Fallback&quot; shall mean that each party will, promptly upon becoming aware of a
      Market Disruption Event, negotiate in good faith with the other to agree on a replacement for the Floating
      Price subject to the Market disruption Event; provided however that is the parties shall not reach an
      agreement within thirty (30) calendar days from the initiation of negotiations, the Dispute Resolutions
      provisions of Section 29 shall apply.
    </p>
    <p>
      (c) &quot;No Fault Termination&quot; means that this Agreement shall be terminated, in accordance with the
      provisions hereof, as if a Force Majeure event shall have occurred.
    </p>
  </>

  return <InlineHelp title={title} content={content}/>
}

export {
  PriceBundledHelp,
  CreditRatingHelp,
  DisruptionFallbackHelp
}
